// ─── seo/Seo.jsx ─────────────────────────────────────────────────────────────
// Drop-in <head> manager for a page. Handles the four things every public page
// needs — title, description, canonical URL, and social card tags — plus
// optional JSON-LD structured data.
//
// Because the app is client-rendered, these tags only reach crawlers that run
// JavaScript. The build-time prerender (server/client/scripts/prerender.js)
// bakes them into real HTML files for the static public routes, which is what
// non-JS crawlers and link unfurlers (LinkedIn, Slack, iMessage) actually read.
import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME, SITE_URL, DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE } from './siteMeta';

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd = null,
  children,
}) {
  // The home page shouldn't read "UniFreelancer | UniFreelancer".
  const fullTitle = title && title !== SITE_NAME ? `${title} | ${SITE_NAME}` : SITE_NAME;

  // Canonical tells Google which URL is authoritative when the same content is
  // reachable through query strings or trailing-slash variants.
  const canonical = path ? `${SITE_URL}${path === '/' ? '' : path}` : undefined;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {canonical && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph — LinkedIn, Facebook, Slack, iMessage */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={image} />

      {/* Twitter/X — summary_large_image renders the full-width card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      {children}
    </Helmet>
  );
}

Seo.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  path: PropTypes.string,
  image: PropTypes.string,
  type: PropTypes.string,
  noindex: PropTypes.bool,
  jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  children: PropTypes.node,
};
